import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  STUDY_MATERIALS, MY_SAVED_MATERIALS, MY_UPLOADED_MATERIALS,
  UPLOAD_STUDY_MATERIAL, LIKE_STUDY_MATERIAL, SAVE_STUDY_MATERIAL,
  INCREMENT_MATERIAL_DOWNLOAD, DELETE_STUDY_MATERIAL,
  MATERIAL_REQUESTS, MY_MATERIAL_REQUESTS, REQUEST_MATERIAL, FULFILL_MATERIAL_REQUEST,
} from '../graphql';
import {
  Library, Upload, Search, SlidersHorizontal, Heart, Bookmark, BookmarkCheck,
  ExternalLink, Trash2, X, Loader2, FileText, BookOpen, Presentation,
  Youtube, FileQuestion, ClipboardList, FileCheck2, ScrollText, File, Sparkles,
  Download, Eye, HandHelping, Link2, CheckCircle2, Inbox,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const MATERIAL_TYPES = [
  { value: 'NOTES',        label: 'Notes',        icon: ScrollText,    color: 'text-brand-600 bg-brand-50' },
  { value: 'PDF',          label: 'PDF',          icon: FileText,      color: 'text-red-600 bg-red-50' },
  { value: 'BOOK',         label: 'Book',         icon: BookOpen,      color: 'text-emerald-600 bg-emerald-50' },
  { value: 'SLIDES',       label: 'Slides',       icon: Presentation,  color: 'text-amber-600 bg-amber-50' },
  { value: 'VIDEO',        label: 'Video',        icon: Youtube,       color: 'text-rose-600 bg-rose-50' },
  { value: 'QUESTION_BANK',label: 'Question Bank',icon: FileQuestion,  color: 'text-brand-600 bg-brand-50' },
  { value: 'ASSIGNMENT',   label: 'Assignment',   icon: ClipboardList, color: 'text-blue-600 bg-blue-50' },
  { value: 'SOLVED_PAPER', label: 'Solved Paper', icon: FileCheck2,    color: 'text-teal-600 bg-teal-50' },
  { value: 'SUMMARY',      label: 'Summary',      icon: ScrollText,    color: 'text-orange-600 bg-orange-50' },
  { value: 'OTHER',        label: 'Other',        icon: File,          color: 'text-gray-600 bg-gray-50' },
];

const SEMESTERS = [1, 2, 3, 4, 5, 6, 7, 8];

const typeInfo = (type) => MATERIAL_TYPES.find(t => t.value === type) || MATERIAL_TYPES[MATERIAL_TYPES.length - 1];

const fmtDate = (d) => {
  const n = Number(d);
  const date = new Date(Number.isNaN(n) ? d : n);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const ALL_MATERIAL_REFETCH = ['StudyMaterials', 'MySavedMaterials', 'MyUploadedMaterials'];

// ── Material Card ────────────────────────────────────────────────────────────
function MaterialCard({ material, currentUserId, onLike, onSave, onOpen, onDelete }) {
  const info = typeInfo(material.materialType);
  const TypeIcon = info.icon;
  const isOwn = String(material.uploader?.id) === String(currentUserId);
  const openUrl = material.fileUrl || material.sourceUrl;

  return (
    <div className="card p-4 hover:shadow-md transition-shadow flex flex-col">
      {/* Type badge + curated + delete */}
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${info.color}`}>
          <TypeIcon size={11} /> {info.label}
        </span>
        {material.isCurated && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-brand-700 bg-brand-100">
            <Sparkles size={11} /> Curated
          </span>
        )}
        {isOwn && (
          <button
            onClick={() => onDelete(material.id)}
            title="Delete upload"
            className="ml-auto p-1 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* Title + description */}
      <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2">{material.title}</h3>
      {material.description && (
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{material.description}</p>
      )}

      {/* Subject / semester / branch chips */}
      <div className="flex flex-wrap gap-1 mt-2">
        <span className="text-xs px-2 py-0.5 rounded-full bg-brand-50 text-brand-600 font-medium">{material.subject}</span>
        {material.semester && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-500">Sem {material.semester}</span>}
        {material.branch && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-500">{material.branch}</span>}
        {material.pages ? <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-400">{material.pages} pg</span> : null}
      </div>

      {/* Tags */}
      {material.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {material.tags.slice(0, 3).map(t => (
            <span key={t} className="text-xs text-gray-400">#{t}</span>
          ))}
          {material.tags.length > 3 && <span className="text-xs text-gray-300">+{material.tags.length - 3}</span>}
        </div>
      )}

      {/* Uploader */}
      <div className="flex items-center gap-1.5 mt-3 mb-2">
        <div className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-[10px] flex-none">
          {material.uploader?.name?.[0]?.toUpperCase() || '?'}
        </div>
        <span className="text-xs text-gray-500 truncate">
          {material.uploader?.name}{material.uploader?.college ? ` · ${material.uploader.college}` : ''}
        </span>
        <span className="text-xs text-gray-300 ml-auto flex-none">{fmtDate(material.createdAt)}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 border-t border-gray-50 pt-3 mt-auto">
        <button
          onClick={() => onLike(material.id)}
          className={`flex items-center gap-1 text-xs font-medium transition-colors ${material.hasLiked ? 'text-rose-500' : 'text-gray-400 hover:text-rose-400'}`}
        >
          <Heart size={14} fill={material.hasLiked ? 'currentColor' : 'none'} /> {material.likes}
        </button>
        <button
          onClick={() => onSave(material.id)}
          title={material.hasSaved ? 'Remove from saved' : 'Save'}
          className={`flex items-center gap-1 text-xs font-medium transition-colors ${material.hasSaved ? 'text-amber-600' : 'text-gray-400 hover:text-amber-500'}`}
        >
          {material.hasSaved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
        </button>
        <span className="text-xs text-gray-300 flex items-center gap-0.5"><Download size={11} /> {material.downloads}</span>
        <span className="text-xs text-gray-300 flex items-center gap-0.5"><Eye size={11} /> {material.views}</span>
        {openUrl ? (
          <button
            onClick={() => onOpen(material)}
            className="ml-auto flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800 bg-brand-50 hover:bg-brand-100 px-2.5 py-1 rounded-xl transition-colors"
          >
            <ExternalLink size={12} /> Open
          </button>
        ) : (
          <span className="ml-auto text-xs text-gray-300">No link</span>
        )}
      </div>
    </div>
  );
}

// ── Upload Modal ─────────────────────────────────────────────────────────────
function UploadModal({ onClose }) {
  const [form, setForm] = useState({
    title: '', description: '', subject: '', semester: '', branch: '',
    materialType: 'NOTES', fileUrl: '', sourceUrl: '', tags: '',
  });
  const [error, setError] = useState('');
  const [upload, { loading }] = useMutation(UPLOAD_STUDY_MATERIAL, {
    refetchQueries: ALL_MATERIAL_REFETCH,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.fileUrl.trim() && !form.sourceUrl.trim()) {
      setError('Add a file URL or a source link so others can open the material.');
      return;
    }
    try {
      await upload({
        variables: {
          title: form.title.trim(),
          description: form.description.trim() || null,
          subject: form.subject.trim(),
          semester: form.semester ? parseInt(form.semester, 10) : null,
          branch: form.branch.trim() || null,
          materialType: form.materialType,
          fileUrl: form.fileUrl.trim() || null,
          sourceUrl: form.sourceUrl.trim() || null,
          tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        },
      });
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Upload size={18} className="text-brand-600" />
            <h2 className="text-lg font-bold text-gray-900">Share a Study Material</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Title *</label>
              <input className="input" required value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. DSA Unit 3 — Sorting notes (complete)" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subject *</label>
              <input className="input" required value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="e.g. Data Structures" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Semester</label>
              <select className="input" value={form.semester} onChange={e => set('semester', e.target.value)}>
                <option value="">Not specific</option>
                {SEMESTERS.map(s => <option key={s} value={s}>Semester {s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Branch</label>
              <input className="input" value={form.branch} onChange={e => set('branch', e.target.value)} placeholder="e.g. CSE, ECE" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
              <input className="input" value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="sorting, exam, unit-3" />
            </div>
          </div>

          {/* Material type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Material Type</label>
            <div className="flex flex-wrap gap-2">
              {MATERIAL_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => set('materialType', t.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    form.materialType === t.value ? t.color + ' border-current' : 'text-gray-500 bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <t.icon size={13} /> {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea className="input" rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="What does it cover? Which units, which exam…" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">File URL</label>
              <input className="input text-sm" type="url" value={form.fileUrl} onChange={e => set('fileUrl', e.target.value)} placeholder="https://drive.google.com/…" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Source link</label>
              <input className="input text-sm" type="url" value={form.sourceUrl} onChange={e => set('sourceUrl', e.target.value)} placeholder="https://… (article / playlist / site)" />
            </div>
          </div>
          <p className="text-xs text-gray-400 -mt-2">Provide at least one of the two links above.</p>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading ? 'Sharing…' : 'Share Material'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Requests Tab ─────────────────────────────────────────────────────────────
function RequestsTab({ currentUserId }) {
  const [showForm, setShowForm] = useState(false);
  const [fulfillingId, setFulfillingId] = useState(null);
  const [fulfillMaterialId, setFulfillMaterialId] = useState('');
  const [form, setForm] = useState({ subject: '', description: '', semester: '', materialType: 'NOTES' });
  const [error, setError] = useState('');

  const { data, loading, error: qError } = useQuery(MATERIAL_REQUESTS, {
    variables: { status: 'OPEN' },
    fetchPolicy: 'cache-and-network',
  });
  const { data: mineData } = useQuery(MY_MATERIAL_REQUESTS, { fetchPolicy: 'cache-and-network' });
  const { data: myUploadsData } = useQuery(MY_UPLOADED_MATERIALS);

  const [requestMaterial, { loading: requesting }] = useMutation(REQUEST_MATERIAL, {
    refetchQueries: ['MaterialRequests', 'MyMaterialRequests'],
  });
  const [fulfillRequest, { loading: fulfilling }] = useMutation(FULFILL_MATERIAL_REQUEST, {
    refetchQueries: ['MaterialRequests', 'MyMaterialRequests'],
  });

  const requests = data?.materialRequests || [];
  const myRequests = mineData?.myMaterialRequests || [];
  const myUploads = myUploadsData?.myUploadedMaterials || [];

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await requestMaterial({
        variables: {
          subject: form.subject.trim(),
          description: form.description.trim(),
          semester: form.semester ? parseInt(form.semester, 10) : null,
          materialType: form.materialType,
        },
      });
      setForm({ subject: '', description: '', semester: '', materialType: 'NOTES' });
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFulfill = async (requestId) => {
    if (!fulfillMaterialId) return;
    setError('');
    try {
      await fulfillRequest({ variables: { requestId, materialId: fulfillMaterialId } });
      setFulfillingId(null);
      setFulfillMaterialId('');
    } catch (err) {
      setError(err.message);
    }
  };

  const STATUS_STYLES = {
    OPEN: 'bg-emerald-50 text-emerald-700',
    FULFILLED: 'bg-brand-50 text-brand-700',
    CLOSED: 'bg-gray-100 text-gray-500',
  };

  return (
    <div className="space-y-4">
      {/* Request form toggle */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">
          {loading && !requests.length ? 'Loading requests…' : `${requests.length} open request${requests.length !== 1 ? 's' : ''}`}
        </p>
        <button onClick={() => setShowForm(s => !s)} className="btn-secondary flex items-center gap-1.5 text-sm">
          {showForm ? <X size={14} /> : <HandHelping size={14} />} {showForm ? 'Cancel' : 'Request a material'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleRequest} className="card p-4 space-y-3">
          <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
            <HandHelping size={15} className="text-brand-600" /> What are you looking for?
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subject *</label>
              <input className="input text-sm" required value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="e.g. Operating Systems" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Semester</label>
              <select className="input text-sm" value={form.semester} onChange={e => set('semester', e.target.value)}>
                <option value="">Any</option>
                {SEMESTERS.map(s => <option key={s} value={s}>Sem {s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select className="input text-sm" value={form.materialType} onChange={e => set('materialType', e.target.value)}>
                {MATERIAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Describe it *</label>
            <textarea className="input text-sm" rows={2} required value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. Need previous year solved papers for OS end-sem (2023–24)…" />
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex justify-end">
            <button type="submit" disabled={requesting} className="btn-primary text-sm">
              {requesting ? 'Posting…' : 'Post Request'}
            </button>
          </div>
        </form>
      )}

      {qError && <p className="text-sm text-red-500">Couldn't load requests: {qError.message}</p>}

      {/* Open requests */}
      {loading && !requests.length ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-brand-400" /></div>
      ) : requests.length === 0 && !qError ? (
        <div className="card p-10 text-center">
          <Inbox size={40} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No open requests</p>
          <p className="text-gray-400 text-sm mt-1">Looking for something? Post the first request.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {requests.map(r => {
            const isMine = String(r.requester?.id) === String(currentUserId);
            return (
              <div key={r.id} className="card p-4">
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-brand-50 text-brand-600 font-medium">{r.subject}</span>
                  {r.semester && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-500">Sem {r.semester}</span>}
                  {r.branch && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-500">{r.branch}</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ml-auto ${STATUS_STYLES[r.status] || STATUS_STYLES.CLOSED}`}>{r.status}</span>
                </div>
                <p className="text-sm text-gray-700">{r.description}</p>
                <div className="flex items-center gap-1.5 mt-2.5">
                  <div className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-[10px]">
                    {r.requester?.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <span className="text-xs text-gray-500 truncate">
                    {r.requester?.name}{r.requester?.college ? ` · ${r.requester.college}` : ''}
                  </span>
                  <span className="text-xs text-gray-300 ml-auto">{fmtDate(r.createdAt)}</span>
                </div>

                {/* Fulfill */}
                {r.status === 'OPEN' && !isMine && (
                  fulfillingId === r.id ? (
                    <div className="mt-3 pt-3 border-t border-gray-50 space-y-2">
                      <label className="block text-xs font-medium text-gray-700">Link one of your uploads</label>
                      {myUploads.length === 0 ? (
                        <p className="text-xs text-gray-400">You haven't uploaded any materials yet — share one first, then fulfill this request.</p>
                      ) : (
                        <select className="input text-sm" value={fulfillMaterialId} onChange={e => setFulfillMaterialId(e.target.value)}>
                          <option value="">Choose a material…</option>
                          {myUploads.map(m => <option key={m.id} value={m.id}>{m.title} ({m.subject})</option>)}
                        </select>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleFulfill(r.id)}
                          disabled={fulfilling || !fulfillMaterialId}
                          className="btn-primary text-xs flex-1 flex items-center justify-center gap-1 disabled:opacity-40"
                        >
                          <Link2 size={12} /> {fulfilling ? 'Linking…' : 'Fulfill'}
                        </button>
                        <button onClick={() => { setFulfillingId(null); setFulfillMaterialId(''); }} className="btn-secondary text-xs flex-1">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setFulfillingId(r.id); setFulfillMaterialId(''); }}
                      className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 px-3 py-1.5 rounded-xl transition-colors"
                    >
                      <CheckCircle2 size={13} /> I have this — Fulfill
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* My requests */}
      {myRequests.length > 0 && (
        <div className="pt-2">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">My requests</h3>
          <div className="space-y-2">
            {myRequests.map(r => (
              <div key={r.id} className="card p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{r.subject}{r.semester ? ` · Sem ${r.semester}` : ''}</p>
                  <p className="text-xs text-gray-400 line-clamp-1">{r.description}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-none ${STATUS_STYLES[r.status] || STATUS_STYLES.CLOSED}`}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function MaterialsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('all'); // all | saved | mine | requests
  const [showUpload, setShowUpload] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ search: '', subject: '', semester: '', materialType: '' });

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const vars = {
    search: filters.search || undefined,
    subject: filters.subject || undefined,
    semester: filters.semester ? parseInt(filters.semester, 10) : undefined,
    materialType: filters.materialType || undefined,
  };

  const allQ = useQuery(STUDY_MATERIALS, { variables: vars, fetchPolicy: 'cache-and-network', skip: tab !== 'all' });
  const savedQ = useQuery(MY_SAVED_MATERIALS, { fetchPolicy: 'cache-and-network', skip: tab !== 'saved' });
  const mineQ = useQuery(MY_UPLOADED_MATERIALS, { fetchPolicy: 'cache-and-network', skip: tab !== 'mine' });

  const [like] = useMutation(LIKE_STUDY_MATERIAL, { refetchQueries: ALL_MATERIAL_REFETCH });
  const [save] = useMutation(SAVE_STUDY_MATERIAL, { refetchQueries: ALL_MATERIAL_REFETCH });
  const [incrementDownload] = useMutation(INCREMENT_MATERIAL_DOWNLOAD, { refetchQueries: ALL_MATERIAL_REFETCH });
  const [deleteMaterial] = useMutation(DELETE_STUDY_MATERIAL, { refetchQueries: ALL_MATERIAL_REFETCH });

  const matchesFilters = (m) => {
    if (filters.subject && !m.subject?.toLowerCase().includes(filters.subject.toLowerCase())) return false;
    if (filters.semester && m.semester !== parseInt(filters.semester, 10)) return false;
    if (filters.materialType && m.materialType !== filters.materialType) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const hay = `${m.title} ${m.description || ''} ${m.subject} ${(m.tags || []).join(' ')}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };

  let materials = [];
  let loading = false;
  let queryError = null;
  if (tab === 'all') {
    materials = allQ.data?.studyMaterials || [];
    loading = allQ.loading && !allQ.data;
    queryError = allQ.error;
  } else if (tab === 'saved') {
    materials = (savedQ.data?.mySavedMaterials || []).filter(matchesFilters);
    loading = savedQ.loading && !savedQ.data;
    queryError = savedQ.error;
  } else if (tab === 'mine') {
    materials = (mineQ.data?.myUploadedMaterials || []).filter(matchesFilters);
    loading = mineQ.loading && !mineQ.data;
    queryError = mineQ.error;
  }

  const handleOpen = (material) => {
    const url = material.fileUrl || material.sourceUrl;
    if (!url) return;
    incrementDownload({ variables: { materialId: material.id } }).catch(() => {});
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this material? This cannot be undone.')) return;
    try {
      await deleteMaterial({ variables: { materialId: id } });
    } catch (err) {
      alert(err.message);
    }
  };

  const activeFilterCount = ['subject', 'semester', 'materialType'].filter(k => filters[k]).length;

  const TABS = [
    { key: 'all', label: 'All' },
    { key: 'saved', label: 'Saved' },
    { key: 'mine', label: 'My uploads' },
    { key: 'requests', label: 'Requests' },
  ];

  const EMPTY_COPY = {
    all: { title: 'No materials found', sub: 'Try clearing filters, or be the first to share.' },
    saved: { title: 'Nothing saved yet', sub: 'Bookmark materials from the All tab and they will show up here.' },
    mine: { title: 'You haven’t uploaded anything yet', sub: 'Share your notes, PDFs and links to help your juniors.' },
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Library className="text-brand-600" size={26} /> Study Materials
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Browse &amp; share notes, PDFs, question banks and links with your campus</p>
        </div>
        <button onClick={() => setShowUpload(true)} className="btn-primary flex items-center gap-2">
          <Upload size={16} /> Share Material
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              tab === t.key ? 'bg-brand-600 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-brand-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'requests' ? (
        <RequestsTab currentUserId={user?.id} />
      ) : (
        <>
          {/* Search + filters */}
          <div className="card p-3 space-y-3">
            <div className="flex gap-2 flex-wrap">
              <div className="flex-1 min-w-48 relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  className="input pl-9 py-2 text-sm"
                  placeholder="Search titles, subjects, tags…"
                  value={filters.search}
                  onChange={e => setF('search', e.target.value)}
                />
              </div>
              <button
                onClick={() => setShowFilters(f => !f)}
                className={`btn-secondary flex items-center gap-1.5 text-sm py-2 ${activeFilterCount > 0 ? 'border-brand-400 text-brand-600' : ''}`}
              >
                <SlidersHorizontal size={15} /> Filters
                {activeFilterCount > 0 && (
                  <span className="bg-brand-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{activeFilterCount}</span>
                )}
              </button>
            </div>

            {showFilters && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t border-gray-100">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Subject</label>
                  <input className="input text-sm py-1.5" value={filters.subject} onChange={e => setF('subject', e.target.value)} placeholder="e.g. Maths" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Semester</label>
                  <select className="input text-sm py-1.5" value={filters.semester} onChange={e => setF('semester', e.target.value)}>
                    <option value="">All Semesters</option>
                    {SEMESTERS.map(s => <option key={s} value={s}>Sem {s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Type</label>
                  <select className="input text-sm py-1.5" value={filters.materialType} onChange={e => setF('materialType', e.target.value)}>
                    <option value="">All Types</option>
                    {MATERIAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <button
                  onClick={() => setFilters({ search: '', subject: '', semester: '', materialType: '' })}
                  className="text-xs text-brand-600 hover:underline col-span-full text-left"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* Type quick pills */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setF('materialType', '')}
              className={`flex-none px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                !filters.materialType ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-500 border-gray-200 hover:border-gray-400'
              }`}
            >
              All
            </button>
            {MATERIAL_TYPES.map(t => (
              <button
                key={t.value}
                onClick={() => setF('materialType', filters.materialType === t.value ? '' : t.value)}
                className={`flex-none flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  filters.materialType === t.value ? t.color + ' border-current' : 'text-gray-500 border-gray-200 hover:border-gray-400'
                }`}
              >
                <t.icon size={12} /> {t.label}
              </button>
            ))}
          </div>

          <p className="text-sm text-gray-400">
            {loading ? 'Loading…' : `${materials.length} material${materials.length !== 1 ? 's' : ''} found`}
          </p>

          {/* Content */}
          {queryError ? (
            <div className="card p-8 text-center">
              <p className="text-red-500 text-sm font-medium">Couldn't load materials</p>
              <p className="text-gray-400 text-xs mt-1">{queryError.message}</p>
            </div>
          ) : loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card p-4 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-2 w-1/3" />
                  <div className="h-4 bg-gray-200 rounded mb-2 w-3/4" />
                  <div className="h-3 bg-gray-100 rounded mb-6 w-full" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : materials.length === 0 ? (
            <div className="card p-12 text-center">
              <Library size={48} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">{EMPTY_COPY[tab].title}</p>
              <p className="text-gray-400 text-sm mt-1">{EMPTY_COPY[tab].sub}</p>
              {tab !== 'saved' && (
                <button onClick={() => setShowUpload(true)} className="btn-primary mt-4">Share a Material</button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {materials.map(m => (
                <MaterialCard
                  key={m.id}
                  material={m}
                  currentUserId={user?.id}
                  onLike={id => like({ variables: { materialId: id } }).catch(() => {})}
                  onSave={id => save({ variables: { materialId: id } }).catch(() => {})}
                  onOpen={handleOpen}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </>
      )}

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} />}
    </div>
  );
}
